import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Header, Footer } from '@/components/marketing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Mail,
  MessageSquare,
  Building2,
  HelpCircle,
  Send,
  Check,
} from 'lucide-react';

export const Route = createFileRoute('/contact')({
  component: ContactPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      type: typeof search.type === 'string' ? search.type : undefined,
    };
  },
});

function ContactPage() {
  const { type } = useSearch({ from: '/contact' });
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: type === 'enterprise' ? 'Enterprise Inquiry' : '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, this would send to an API
    console.log('Form submitted:', formData);
    setSubmitted(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="pt-32 pb-16 sm:pt-40 sm:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
                Get in Touch
              </h1>
              <p className="mt-6 text-xl text-muted-foreground">
                Have questions? We'd love to hear from you. Send us a message and
                we'll respond as soon as possible.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Options */}
        <section className="py-8">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-16">
              {[
                {
                  icon: MessageSquare,
                  title: 'General Inquiry',
                  description: 'Questions about ForgeNetwork',
                  action: 'hello@forgenetwork.pro',
                },
                {
                  icon: Building2,
                  title: 'Enterprise Sales',
                  description: 'Discuss enterprise needs',
                  action: 'sales@forgenetwork.pro',
                },
                {
                  icon: HelpCircle,
                  title: 'Support',
                  description: 'Get help with your account',
                  action: 'support@forgenetwork.pro',
                },
                {
                  icon: Mail,
                  title: 'Press',
                  description: 'Media and press inquiries',
                  action: 'press@forgenetwork.pro',
                },
              ].map((option) => (
                <div
                  key={option.title}
                  className="rounded-xl border bg-card p-6 text-center hover:border-primary/50 transition-colors"
                >
                  <option.icon className="h-8 w-8 text-primary mx-auto" />
                  <h2 className="mt-4 font-semibold">{option.title}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {option.description}
                  </p>
                  <a
                    href={`mailto:${option.action}`}
                    className="mt-3 inline-block text-sm text-primary hover:underline"
                  >
                    {option.action}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl">
              <div className="rounded-xl border bg-card p-8 sm:p-12">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 mb-6">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold">Message Sent!</h3>
                    <p className="mt-2 text-muted-foreground">
                      Thanks for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <Link to="/">
                      <Button className="mt-6">Back to Home</Button>
                    </Link>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-center mb-8">
                      Send Us a Message
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name</Label>
                          <Input
                            id="name"
                            name="name"
                            placeholder="Your name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="you@company.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company">Company (optional)</Label>
                        <Input
                          id="company"
                          name="company"
                          placeholder="Your company"
                          value={formData.company}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          required
                        >
                          <option value="">Select a topic</option>
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Enterprise Inquiry">Enterprise Inquiry</option>
                          <option value="Technical Support">Technical Support</option>
                          <option value="Partnership">Partnership</option>
                          <option value="Feedback">Feedback</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <textarea
                          id="message"
                          name="message"
                          placeholder="How can we help?"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                          required
                        />
                      </div>

                      <Button type="submit" size="lg" className="w-full gap-2">
                        <Send className="h-4 w-4" />
                        Send Message
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
